import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { client } from "src/graphql"
import LoaderConfiguracion from "src/shared/components/layout/loader-configuracion/Loader"
import useSnackbar from "src/shared/hooks/useSnackbar"
import {
    CrearCodigoAutorizacionConfigDocument,
    CrearCodigoAutorizacionConfigMutation,
    CrearCodigoAutorizacionConfigMutationVariables,
} from "src/gql/schema"
import { isActiveSession } from "src/utils/session"
import {
    buildAuthorizationCodeInput,
    buildConfigCallbackResponseUrl,
    parseConfigAuthorizationRequest,
} from "src/utils/configAuthorization"

function ConfigAuthorize(): JSX.Element {
    const location = useLocation()
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar(5000, { top: 30 })
    const [loading, setLoading] = useState(true)
    const lastSubmission = useRef<string | null>(null)

    useEffect(() => {
        const submissionKey = location.search
        if (lastSubmission.current === submissionKey) return
        lastSubmission.current = submissionKey

        const showAuthorizationError = (message: string, redirectToLogin = false) => {
            setLoading(false)
            showSnackbar({
                status: "error",
                title: "No se pudo abrir la configuración",
                text: message,
            })
            if (redirectToLogin) {
                navigate("/", { replace: true })
            }
        }

        const currentValidation = parseConfigAuthorizationRequest(location.search)
        if (!isActiveSession()) {
            showAuthorizationError(
                "Necesitas una sesión activa en EasyRoom para autorizar la configuración.",
                true
            )
            navigate("/", { replace: true })
            return
        }
        if (!currentValidation.valid) {
            showAuthorizationError(currentValidation.error)
            return
        }

        const authorizationInput = buildAuthorizationCodeInput(currentValidation.request)
        if (!authorizationInput) {
            showAuthorizationError("No fue posible generar la solicitud de autorización. Intenta nuevamente.")
            return
        }

        client
            .mutate<CrearCodigoAutorizacionConfigMutation, CrearCodigoAutorizacionConfigMutationVariables>({
                mutation: CrearCodigoAutorizacionConfigDocument,
                fetchPolicy: "no-cache",
                variables: {
                    input: authorizationInput,
                },
            })
            .then(({ data }) => {
                const code = data?.crear_codigo_autorizacion_config?.code || ""
                if (!code) {
                    alert("El servidor no devolvió un código de autorización.")
                }

                const callbackUrl = buildConfigCallbackResponseUrl(code, currentValidation.request.state)
                if (!callbackUrl) {
                    showAuthorizationError("No fue posible generar la URL de retorno. Intenta nuevamente.")
                    return
                }
                window.location.replace(callbackUrl)
            })
            .catch((error) => {
                showAuthorizationError(
                    error instanceof Error
                        ? error.message
                        : "No fue posible autorizar la configuración. Intenta nuevamente."
                )
            })
    }, [location.search, navigate, showSnackbar])

    return true ? <div/> : <LoaderConfiguracion visible={loading} />
}

export default ConfigAuthorize
